import { FeatureName, useIsFeatureEnabled } from '@gripp/shared-logic';
import { useEffect, useState } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { Card } from 'react-native-paper';
import { useOpenedState } from '../../hooks/useOpenedState';
import { Colors } from '../../themes/colors';
import { ImageComponent as ImageComponentCropCircle } from '../crop-circle/Components/Image';
import { ImageLightBox } from '../modalImages/imageLightbox';
import { ImageThumbnail } from './imageThumbnail';
import { OutlinedImage } from './outlinedImage';
import { ProcessingPlaceholder } from './processingPlaceholder';
import {
  ImageData,
  MediaComponent,
  MediaComponentProps,
  SizePrefix,
} from './types';

export const ImageComponent: MediaComponent<ImageData> = ({
  hasLightbox = true,
  displayMode = 'card',
  displayWidth,
  onPress,
  canDelete,
  ...props
}: MediaComponentProps<ImageData>) => {
  const { isOpened, onOpen, onClose } = useOpenedState();
  const [showLightbox, setShowLightbox] = useState(false);
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  const defaultSize = isCropCircleRedesignEnabled ? 88 : 80;
  const displaySize = displayWidth ?? defaultSize;

  useEffect(() => {
    setShowLightbox(hasLightbox && !props.mediaItem.isUploading);
  }, [hasLightbox, props.mediaItem.isUploading]);

  const handlePress = () => {
    if (onPress) {
      onPress();
      return;
    }
    onOpen();
  };

  // TODO: Crop Circle Redesign | Remove conditional rendering when feature is fully launched
  if (!isCropCircleRedesignEnabled) {
    return (
      <ProcessingPlaceholder
        processing={props.mediaItem.showProcessing ?? false}
        width={displaySize}
        height={displaySize}
        displayMode={displayMode}
        url={props.mediaItem.url}
        name={props.mediaItem.name ?? ''}
        extension={props.mediaItem.extension}
      >
        {displayMode === 'outlined' ? (
          <Pressable onPress={handlePress} style={{ width: displaySize }}>
            <OutlinedImage
              url={props.mediaItem.urlsBySize?.[SizePrefix.small]}
              size={displaySize}
            />
          </Pressable>
        ) : (
          <Card
            onPress={handlePress}
            style={[
              styles.imageCard,
              { width: displaySize, height: displaySize },
            ]}
          >
            <ImageThumbnail
              style={[
                styles.imageThumbnail,
                { width: displaySize, height: displaySize },
              ]}
              imageUri={props.mediaItem.urlsBySize?.[SizePrefix.extraSmall]}
              progress={props.mediaItem.uploadProgress}
              uploading={props.mediaItem.isUploading}
            />
          </Card>
        )}

        {showLightbox && (
          <ImageLightBox
            url={props.mediaItem.urlsBySize?.[SizePrefix.original]}
            isOpened={isOpened}
            onClose={onClose}
          />
        )}
      </ProcessingPlaceholder>
    );
  }

  return (
    <ProcessingPlaceholder
      processing={props.mediaItem.showProcessing ?? false}
      width={displaySize}
      height={displaySize}
      displayMode={displayMode}
      url={props.mediaItem.url}
      name={props.mediaItem.name ?? ''}
      extension={props.mediaItem.extension}
    >
      {displayMode === 'outlined' ? (
        <Pressable onPress={handlePress} style={{ width: displaySize }}>
          <OutlinedImage
            url={props.mediaItem.urlsBySize?.[SizePrefix.small]}
            size={displaySize}
          />
        </Pressable>
      ) : (
        <ImageComponentCropCircle
          size={displaySize}
          borderRadius={4}
          source={{
            uri: props.mediaItem.urlsBySize?.[SizePrefix.extraSmall],
          }}
          progress={props.mediaItem.uploadProgress}
          uploading={props.mediaItem.isUploading}
          onPress={handlePress}
          onClose={() => props.deleteMediaItem?.(props.mediaItem.id)}
          showClose={!!canDelete}
        />
      )}

      {showLightbox && (
        <ImageLightBox
          url={props.mediaItem.urlsBySize?.[SizePrefix.original]}
          isOpened={isOpened}
          onClose={onClose}
        />
      )}
    </ProcessingPlaceholder>
  );
};

const styles = StyleSheet.create({
  imageCard: {
    height: 80,
    width: 80,
    borderRadius: 8,
    border: 0.5,
    borderColor: Colors.grayDivider,
    cursor: 'pointer',
  },
  imageThumbnail: {
    height: 80,
    width: 80,
    borderRadius: 8,
  },
});
